import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionFournitures.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import fournitureService from '../../services/fournitureService';

// Vector SVG Icons
const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

function GestionFournitures() {
  const [fournitures, setFournitures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCycle, setFilterCycle] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    titre: '',
    classe: '',
    cycle: 'college',
    annee_scolaire: '2026-2027',
    description: '',
    articles: [''],
    publie: true
  });

  const loadFournitures = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = filterCycle !== 'all' ? { cycle: filterCycle } : {};
      const res = await fournitureService.admin.getAll(params);
      setFournitures(res.data.data || []);
    } catch (error) {
      console.error('Erreur chargement fournitures admin:', error);
    } finally {
      setLoading(false);
    }
  }, [filterCycle]);

  useEffect(() => {
    loadFournitures();
  }, [loadFournitures]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({
      titre: '',
      classe: '',
      cycle: 'college',
      annee_scolaire: '2026-2027',
      description: '',
      articles: [''],
      publie: true
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      titre: item.titre,
      classe: item.classe,
      cycle: item.cycle,
      annee_scolaire: item.annee_scolaire || '2026-2027',
      description: item.description || '',
      articles: item.articles && item.articles.length > 0 ? item.articles : [''],
      publie: item.publie
    });
    setShowModal(true);
  };

  const handleArticleChange = (index, value) => {
    const updated = [...formData.articles];
    updated[index] = value;
    setFormData({ ...formData, articles: updated });
  };

  const handleAddArticleLine = () => {
    setFormData({ ...formData, articles: [...formData.articles, ''] });
  };

  const handleRemoveArticleLine = (index) => {
    const updated = formData.articles.filter((_, i) => i !== index);
    setFormData({ ...formData, articles: updated.length > 0 ? updated : [''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanArticles = formData.articles.filter(a => a.trim() !== '');
      const payload = {
        ...formData,
        articles: cleanArticles
      };

      if (editingId) {
        await fournitureService.admin.update(editingId, payload);
        alert('Liste de fournitures mise à jour avec succès !');
      } else {
        await fournitureService.admin.create(payload);
        alert('Nouvelle liste de fournitures enregistrée !');
      }

      setShowModal(false);
      loadFournitures();
    } catch (error) {
      console.error('Erreur enregistrement:', error);
      alert("Erreur lors de l'enregistrement de la liste de fournitures.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette liste de fournitures ?')) {
      try {
        await fournitureService.admin.delete(id);
        alert('Liste supprimée avec succès.');
        loadFournitures();
      } catch (error) {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression.');
      }
    }
  };

  return (
    <div className="admin-page-container">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Fournitures & Manuels Scolaires" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />

      <div className="admin-page-content">
        <div className="admin-header-actions">
          <div className="filter-group">
            <label>Filtrer par cycle :</label>
            <select 
              value={filterCycle} 
              onChange={(e) => setFilterCycle(e.target.value)}
              className="admin-select"
            >
              <option value="all">Tous les cycles</option>
              <option value="college">Collège (6ème à 3ème)</option>
              <option value="seconde">Secondes (2nde Générale & Technique)</option>
              <option value="premiere">Premières (1ère Générale & Technique)</option>
              <option value="terminale">Terminales (Tle Générale & Technique)</option>
              <option value="superieur">Supérieur & Formations Pro (DWM, CPFPA)</option>
            </select>
          </div>

          <button className="btn-admin-primary" onClick={handleOpenCreate}>
            <IconPlus />
            <span>Ajouter une liste de fournitures</span>
          </button>
        </div>

        {loading ? (
          <div className="loading-state">Chargement des données...</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Classe</th>
                  <th>Cycle</th>
                  <th>Titre</th>
                  <th>Année</th>
                  <th>Articles</th>
                  <th>Téléchargements</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fournitures.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                      Aucune liste de fournitures trouvée.
                    </td>
                  </tr>
                ) : (
                  fournitures.map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.classe}</strong></td>
                      <td><span className="badge-cycle">{item.cycle}</span></td>
                      <td>{item.titre}</td>
                      <td>{item.annee_scolaire}</td>
                      <td>{item.articles ? item.articles.length : 0} items</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                          <IconDownload />
                          <span>{item.telechargements || 0}</span>
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${item.publie ? 'published' : 'draft'}`}>
                          {item.publie ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button className="btn-action edit" title="Modifier" onClick={() => handleEdit(item)}>
                          <IconEdit />
                        </button>
                        <button className="btn-action delete" title="Supprimer" onClick={() => handleDelete(item.id)}>
                          <IconTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL CREATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Modifier la liste de fournitures' : 'Nouvelle liste de fournitures'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <IconClose />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body modal-form">
              <div className="form-group">
                <label>Titre officiel du document *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="Ex: Liste des Fournitures - Classe de 6ème"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Classe *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="Ex: 6ème A, Seconde C, CP..."
                    value={formData.classe}
                    onChange={(e) => setFormData({ ...formData, classe: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Cycle d'enseignement *</label>
                  <select 
                    value={formData.cycle}
                    onChange={(e) => setFormData({ ...formData, cycle: e.target.value })}
                  >
                    <option value="college">Collège (6ème à 3ème)</option>
                    <option value="seconde">Secondes (2nde A, C, G2, F3, F4, HR, MM-V)</option>
                    <option value="premiere">Premières (1ère A, B, C, D, G2, F3, F4, MM-V)</option>
                    <option value="terminale">Terminales (Tle A, B, C, D, G2, F3, F4)</option>
                    <option value="superieur">Supérieur & Formations Pro (DWM, CPFPA)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Année Scolaire</label>
                  <input 
                    type="text" 
                    placeholder="2026-2027"
                    value={formData.annee_scolaire}
                    onChange={(e) => setFormData({ ...formData, annee_scolaire: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Visibilité</label>
                  <select 
                    value={formData.publie ? '1' : '0'}
                    onChange={(e) => setFormData({ ...formData, publie: e.target.value === '1' })}
                  >
                    <option value="1">Publié (Visible au public)</option>
                    <option value="0">Brouillon (Caché)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Consignes / Description générale</label>
                <textarea 
                  rows="2"
                  placeholder="Notes importantes, recommandations de matériel ou manuels obligatoires..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ margin: 0 }}>Articles & Manuels requis ({formData.articles.length}) :</label>
                  <button type="button" className="btn-add-line" onClick={handleAddArticleLine}>
                    <IconPlus />
                    <span>Ajouter un article</span>
                  </button>
                </div>
                
                <div className="articles-input-list">
                  {formData.articles.map((art, idx) => (
                    <div key={idx} className="article-input-row">
                      <span className="row-num">{idx + 1}.</span>
                      <input 
                        type="text" 
                        placeholder="Ex: 4 cahiers grand format 96 pages / Manuel de Maths CIAM 6e"
                        value={art}
                        onChange={(e) => handleArticleChange(idx, e.target.value)}
                      />
                      <button 
                        type="button" 
                        className="btn-del-line"
                        onClick={() => handleRemoveArticleLine(idx)}
                      >
                        <IconClose />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer la liste</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default GestionFournitures;
